import React, { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import Table from '../../../../../components/Table/Table';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import OutlineButton from '../../../../../components/OutlineButton/OutlineButton';

import AddIcon from '@material-ui/icons/Add';
import AddCredentialDialog from './AddCredentialDialog';
import { doClearCredential, doDeleteCredential, doDownloadCredential, doGetCredential, doRequestBulkStatus } from '../../../../../redux/thunks/credentials';
import { setBulkAddDetails } from '../../../../../redux/actionCreators/credentials';
import { credTypeOptions, credTypes } from './helper';
import { debounce, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Menu, MenuItem } from '@material-ui/core';
import { MoreVert } from '@material-ui/icons';
import { useTableMenuStyles } from '../../../../../utils/TableMenuStyles';
import Button from '../../../../../components/Buttons/Button';
import SuccessTick from '../../../../../assets/svgComponent/SuccessTick';
import FileDownload from '../../../../../assets/svgComponent/FileDownload';
import Typography from '../../../../../components/Typography/Typography';
import { colors } from '../../../../../styles/colors';
import FlexColumn from '../../../../../components/FlexBox/FlexColumn';
import useIsMobile from '../../../../../hooks/useIsMobile';
import MobileTable from '../../../../../components/MobileTable/MobileTable';
import SDialog from '../../../../../components/SDialog/SDialog';
import FlexRow from '../../../../../components/FlexBox/FlexRow';
import SButton from '../../../../../components/SButton/SButton';
import SquareDelete from '../../../../../components/SquareDelete/SquareDelete';

const credTypeMap = { mobile_ble_nfc: 'Mobile', apple_wallet: 'Apple Wallet', qr: 'QR', apple_wallet_watch: 'Apple Wallet (Watch)', apple_wallet_phone: 'Apple Wallet (Phone)' }

const statusOptions = [{ id: 'Activated', name: 'Activated' }, { id: 'Inactive', name: 'Inactive' }]

const CredentialTab = () => {

  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const classes = useTableMenuStyles();

  const prevFiltersRef = useRef(null);

  const [addCredential, setAddCredential] = useState(false);
  const { customerId } = useParams();
  const { credentialList, bulkAddCredentials, organisation } = useSelector((state) => ({
    credentialList: state.credentials.credentialList,
    bulkAddCredentials: state.credentials.bulkAddCredentials,
    organisation: state.organisation.organisation
  }));

  const [menu, setMenu] = useState({ show: false, anchorEl: null, sItem: {} });
  const [credDelete, setCredDelete] = useState({ show: false, row: 0 });
  const [credDeleteSuccess, setCredDeleteSuccess] = useState(false);
  const [pagination, setPagination] = useState({ showNextButton: false, currentPage: 1, perPage: 7 });
  const [filters, setFilters] = useState({
    credentialId: '',
    credentialType: '',
    email: '',
    name: '',
    status: ''
  });

  const getCredentials = (pageToSet, perPageToset, doNotAppend = false) => {
    const reqFilters = {
      credentialId: Number(filters?.credentialId) || undefined,
      credentialType: filters?.credentialType?.id || '',
      email: filters?.email || '',
      name: filters?.name || '',
      status: filters?.status?.id || '',
    }

    const isFiltersChanged =
      prevFiltersRef.current !== null &&
      JSON.stringify(prevFiltersRef.current) !== JSON.stringify(reqFilters);

    const appendData = isFiltersChanged ? false : isMobile;

    dispatch(doGetCredential(customerId,
      { pagination: { page: pageToSet || pagination?.currentPage, perPage: perPageToset || pagination?.perPage }, filters: reqFilters, partnerId: organisation?.generalSetting?.partnerId, },
      (data) => {
        setPagination(prev => ({
          ...prev,
          currentPage: pageToSet || prev?.currentPage,
          perPage: perPageToset || prev?.perPage,
          total: data?.message?.pagination?.total,
          showNextButton: !!data?.message?.pagination?.showNextButton
        }))
      },
      doNotAppend ? false : appendData
    ));

    prevFiltersRef.current = reqFilters;
  }

  const getCredentialsData = useCallback(() => {
    if (!isMobile) {
      getCredentials();
    }
  }, [dispatch, setPagination, customerId, pagination?.currentPage, pagination?.perPage, filters, organisation?.generalSetting?.partnerId, isMobile]);

  const debouncedGetCredentials = useMemo(
    () => debounce(getCredentialsData, 500),
    [getCredentialsData]
  );

  useEffect(() => {
    return () => {
      dispatch(doClearCredential())
    }
  }, [])

  useEffect(() => {
    return () => {
      debouncedGetCredentials.clear();
    };
  }, [debouncedGetCredentials]);

  useEffect(() => {
    debouncedGetCredentials();
  }, [debouncedGetCredentials]);

  useEffect(() => {
    if (isMobile) {
      getCredentials(1, 25)
    }

  }, [isMobile, filters])


  useEffect(() => {
    let intervalId;

    const shouldPoll =
      Number(customerId) === bulkAddCredentials?.orgId &&
      bulkAddCredentials?.requestId &&
      bulkAddCredentials?.total > 0 &&
      bulkAddCredentials.total !== bulkAddCredentials.processed;

    if (shouldPoll) {
      intervalId = setInterval(() => {
        dispatch(doRequestBulkStatus
          (customerId,
            organisation?.generalSetting?.partnerId,
            bulkAddCredentials.requestId, (data) => {
              if (data?.message?.total === data.message?.processed) {
                clearInterval(intervalId);
                getCredentials(null, null, true);
              }
            }))
      }, 2000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [dispatch, bulkAddCredentials, customerId, organisation?.generalSetting?.partnerId]);

  const mobileFiltersConfig = useMemo(() => {
    return [
      { name: "Name", key: "name", type: "search" },
      { name: "Email", key: "email", type: "search" },
      { name: "Credential ID", key: "credentialId", type: "search" },
      { name: "Credential Type", key: "credentialType", type: "select", options: credTypeOptions },
      { name: "Status", key: "status", type: "select", options: statusOptions },
    ]
  }, [])

  const columns = useMemo(() => {
    const commonColumns = [
      {
        title: 'Credential ID',
        field: 'credentialId',
        name: 'credentialId',
        type: 'number'
      },
      {
        title: 'Credential Type',
        field: 'credentialType',
        name: 'credentialType',
        lookup: credTypeOptions,
        render: (columnProps, rowData) => {
          return credTypeMap[rowData?.credentialType] || ""
        },
      },
      {
        title: 'Email',
        field: 'email',
        name: 'email',
      },
      {
        title: 'Name',
        field: 'name',
        name: 'name',
      },
      {
        title: 'Status',
        field: 'status',
        name: 'status',
        lookup: statusOptions
      }
    ]

    const actions = [
      {
        title: 'Action',
        render: (columnProps, data, row) => {
          return (
            <FlexRow justifyContent="end">
              <SquareDelete
                onPress={() => {
                  setCredDelete({ show: true, row });
                }}
                style={{ height: '2rem', width: '2rem', padding: '0' }}
              />
            </FlexRow>
          );
        },
        renderM: (columnProps, rowData, row) => {
          return (<SquareDelete
            onPress={() => {
              setCredDelete({ show: true, row });
            }}
            style={{ height: '2rem', width: '2rem', padding: '0' }}
          />)
        },
        props: {
          head: {
            style: {
              textAlign: 'right'
            }
          },
          cell: {
            style: {
              textAlign: 'right'
            }
          }
        }
      }
    ]


    return {
      allColumns: [...commonColumns, ...actions],
      commonColumns,
      actions
    }

  }, [])

  const hSearchChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }))
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }))
  };

  const search = {
    onChange: hSearchChange,
  };

  const changePage = (p) => {
    const { page, per_page } = p;
    setPagination(prev => ({
      ...prev,
      currentPage: page,
      perPage: per_page
    }))

  };

  const hCloseBulkAddProgress = () => {
    dispatch(setBulkAddDetails({ show: false, requestId: '' }))
  }

  const hDeleteCred = () => {
    const credData = credentialList?.credentials[Number(credDelete?.row)]
    dispatch(doDeleteCredential(
      customerId,
      {
        orgUserId: credData?.orgUserId,
        credentials: [{ credentialType: credData?.credentialType, credentialId: credData?.credentialId }],
        partnerId: organisation?.generalSetting?.partnerId,
      },
      () => {
        setCredDelete({ show: false, row: 0 });
        setCredDeleteSuccess(true);
        getCredentials(null, null, true);
      }
    ))
  }

  const hReportDownload = () => {
    const reqFilters = {
      credentialId: Number(filters?.credentialId) || undefined,
      credentialType: filters?.credentialType?.id || '',
      email: filters?.email || '',
      name: filters?.name || '',
      status: filters?.status?.id || '',
    }
    dispatch(doDownloadCredential(customerId, {
      pagination: { page: -1, perPage: -1 },
      filters: reqFilters,
      partnerId: organisation?.generalSetting?.partnerId,
    }, organisation?.generalSetting?.name))
  }

  return (
    <Fragment>
      <div className="spacer__1" />
      <div>
        {addCredential && (
          <AddCredentialDialog
            onSuccess={() => { getCredentials() }}
            open={addCredential}
            onClose={() => { setAddCredential(false) }}
          />
        )}
        <FlexRow flexWrap='nowrap' justifyContent='space-between' styles={{ marginBottom: '1rem' }}>
          <Typography style={{ whiteSpace: 'nowrap' }} variant={"h7M"} >Credential List</Typography>
          <FlexRow width='fit-content'>
            {!isMobile && <IconButton onClick={hReportDownload}>
              <FileDownload />
            </IconButton>}
            <SButton
              size='small'
              variant={"secondary"}
              color={"info"}
              onClick={() => {
                setAddCredential(true)
              }}
              style={{ padding: isMobile ? '0.25rem 1rem' : '' }}
            >
              <AddIcon style={{ marginRight: '0.5rem', padding: '0' }} /> Credential
            </SButton>
          </FlexRow>
        </FlexRow>
        {Number(customerId) === bulkAddCredentials?.orgId && bulkAddCredentials?.total > 0 && bulkAddCredentials.show && <p style={{
          backgroundColor: bulkAddCredentials?.type === 0 ? '#FFE1CD' : bulkAddCredentials?.type === 1 ? '#ACEBAA' : '#FF7F7F',
          color: bulkAddCredentials?.type === 0 ? '#FF6601' : bulkAddCredentials?.type === 1 ? '#168B0E' : '#FFFFFF',
          position: 'relative',
          padding: '17px',
          borderRadius: '0.5rem',
          marginBottom: '1rem',
        }} className="warning text-center">
          <div onClick={hCloseBulkAddProgress} style={{ position: 'absolute', right: '1rem', top: '25%', cursor: 'pointer', zIndex: 2 }}><HighlightOffIcon /></div>
          Adding Credentials | {bulkAddCredentials?.type === 0 ? 'In progress' : bulkAddCredentials?.type === 1 ? 'Successful' : 'Failed'} {bulkAddCredentials?.type === 2 ? bulkAddCredentials.failed : bulkAddCredentials.processed}/{bulkAddCredentials.total} users. {bulkAddCredentials.type === 2 && ''}
          {/* {bulkAddCredentials?.type == 2 && <OutlineButton onClick={hFailedUsersDownload} style={{ margin: '0', marginLeft: '3rem', color: 'white', border: '1px solid white', padding: '0px 15px', borderRadius: '6px' }} text={"Download"} color='white' />} */}
        </p>}
        <div item xs={12} sm={12} md={12} lg={12}>
          {isMobile ?
            <MobileTable
              columns={columns?.commonColumns}
              data={credentialList?.credentials || []}
              actions={columns?.actions}
              filtersConfig={mobileFiltersConfig}
              filters={filters}
              setFilters={setFilters}
              pagination={pagination}
              changePage={(nextPage) => { getCredentials(nextPage); }}
              emptyMessage={'No Credentials Found'}
            />
            :
            <Table
              columns={columns?.allColumns}
              data={credentialList?.credentials || []}
              defaultMessage={'No Credentials Found'}
              pagination={pagination}
              options={{ search }}
              changePage={changePage}
              showNext={true}
            />}
        </div>
      </div>

      {/* <Menu
        anchorEl={menu.anchorEl}
        keepMounted
        open={menu.show}
        getContentAnchorEl={null}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        classes={{ paper: classes.paper, list: classes.list }}
        onClose={() => { setMenu({ ...menu, show: false }); }}>
        <MenuItem
          className={classes.menuItemRoot}
          style={{ color: 'red' }}
          dense
          onClick={() => {
            setCredDelete(true);
            setMenu({ ...menu, show: false });
          }}>
          Delete
        </MenuItem>
      </Menu> */}

      <SDialog
        title='Delete Credential'
        open={credDelete.show}
        onClose={() => {
          setCredDelete({ show: false, row: 0 });
        }}
        titleConfig={{
          variant: 'h5M'
        }}
        secondaryButtonConfig={{
          text: 'Cancel',
          onClick: () => {
            setCredDelete({ show: false, row: 0 });
          }
        }}
        primaryButtonConfig={{
          text: 'Delete',
          color: 'error',
          onClick: hDeleteCred
        }}
      >
        <FlexColumn>
          <Typography variant="b2R">
            Deleting this credential will remove the user’s access to all doors and remove all user information. Are you sure you want to continue?
          </Typography>
          {credentialList?.credentials?.[Number(credDelete?.row)]?.credentialType?.includes("apple_wallet") && <Typography variant={"b3R"} color={colors.error500}>Note : Deleting an apple wallet credential will remove both the phone and watch credentials associated to the user</Typography>}
        </FlexColumn>
      </SDialog>

      <SDialog
        title='Credential Deleted'
        open={credDeleteSuccess}
        onClose={() => {
          setCredDeleteSuccess(false);
        }}
        titleConfig={{
          variant: 'h5M'
        }}
        primaryButtonConfig={{
          text: 'Done',
          onClick: () => {
            setCredDeleteSuccess(false);
          }
        }}
      >
        <div className='column' style={{ gap: '1rem', width: '100%' }}>
          <SuccessTick />
          <p style={{ fontSize: '0.875rem' }}>Successfully deleted credential</p>
        </div>
      </SDialog>
    </Fragment>
  );
};

export default CredentialTab;
